import { ILocation, Identifiable } from '../../../../lib/types';
import React, { useState } from 'react';
import { RIDialog } from '../../../_core/dialog/dialog.component';
import { TableWithActions } from '../../../_core/table/table-with-actions.component';
import { CreateLocationsContainer } from '../../../domains/locations/create/create-locations-form.container';
import { Button } from '../../../_core/button/button.component';
import { activeStatus } from '../../../../utils/wording.helpers';
import { UpdateLocationContainer } from '../../../domains/locations/update/update-location-form.container';
import { IAdminProps } from '../../../_core/core.types';
import { ITableHeaderCell, ITableOptionsConfig, ITableRowValues } from '../../../_core/table/table.types';
import { nameAndStatusSort, sortMethodTuple, useTableSortConfig } from '../../../_core/table/utils/table-sort';
import { IPermanent } from '../../../domains/request-group/request-group.types';
import { showError } from '../../../../lib/utils';
import { FetchResult } from '@apollo/client';
import { useConfirm } from '../../../_core/confirm/confirm.utils';

export interface ILocationTableProps extends IAdminProps, ITableOptionsConfig<ILocation> {
  locations: ILocation[];
}

interface ILocationTableActionProps {
  deleteLocation: (_: Identifiable & IPermanent) => Promise<FetchResult<{ deleteLocation: ILocation }>>;
  deleting: boolean;
  restoreLocation: (_: Identifiable) => void;
  restoring: boolean;
}

export const LocationTable: React.FC<ILocationTableProps & ILocationTableActionProps> = ({
  locations,
  deleteLocation,
  deleting,
  restoreLocation,
  restoring,
  ...rest
}) => {
  const [createLocations, setCreateLocations] = useState(false);
  const [updateLocation, setUpdateLocation] = useState<ILocation | null>(null);

  const { ConfirmationDialog: PermanentlyDeleteConfirmationDialog, confirm: confirmPermanent } = useConfirm({
    title: 'Are you sure you want to permanently delete this location?',
    message: 'This action cannot be undone. Consider archiving instead.'
  });

  const headers: ITableHeaderCell[] = [
    { content: 'Name', sortKey: 'name' },
    { content: 'Status', sortKey: 'deletedAt' },
    { content: '' }
  ];

  const { sortFunc, ...restSort } = useTableSortConfig({
    defaultSortFunc: nameAndStatusSort,
    sortMethods: {
      name: sortMethodTuple(),
      deletedAt: sortMethodTuple('string', (d) => activeStatus(!d))
    }
  });

  return (
    <>
      <TableWithActions
        {...rest}
        {...restSort}
        bordered
        headers={headers}
        values={
          locations.sort(sortFunc).map(({ _id, name, deletedAt }, i) => {
            const row: ITableRowValues = {
              values: [
                { children: name },
                { children: activeStatus(!deletedAt) },
                {
                  children: (
                    <Button
                      slim
                      loading={deleting || restoring}
                      size="large"
                      text="ACTION"
                      dropdown={{
                        options: [
                          { text: 'Edit', onClick: () => setUpdateLocation(locations[i]) },
                          deletedAt
                            ? { text: 'Reactivate', onClick: () => restoreLocation({ _id }) }
                            : {
                                text: 'Archive',
                                onClick: () =>
                                  deleteLocation({ _id }).catch((err) => showError('Unable to archive location', err))
                              },
                          {
                            text: 'Delete',
                            onClick: () =>
                              confirmPermanent().then((confirmed) => {
                                if (confirmed)
                                  deleteLocation({ _id, permanent: true }).catch((err) =>
                                    showError('Unable to delete location', err)
                                  );
                              })
                          }
                        ]
                      }}
                    />
                  )
                }
              ]
            };

            return row;
          }) ?? []
        }
      >
        <Button onClick={() => setCreateLocations(true)} text="Add Locations" size="large" />
      </TableWithActions>
      <RIDialog open={createLocations} setOpen={setCreateLocations}>
        <CreateLocationsContainer close={() => setCreateLocations(false)} />
      </RIDialog>
      {!!updateLocation && (
        <RIDialog open={!!updateLocation} setOpen={(o) => setUpdateLocation((l) => (o ? l : null))}>
          <UpdateLocationContainer {...updateLocation} />
        </RIDialog>
      )}
      <PermanentlyDeleteConfirmationDialog />
    </>
  );
};
